import { isScalingTableSearch } from '@/itemFinder/itemFinder.utilities';
import { isNull, orFilters, valueFilter } from '@/serverSideTable/materializedTables/filters';
import { GraphQL } from '@/serverSideTable/materializedTables/materializedTables.constants';
import { type FilterSpecV1, toSdkFilter } from '@/tableDefinitionEditor/filterSpec.types';
import { SeeqNames } from '@seeqdev/constants';
import type { Search } from '@/itemFinder/itemFinder.constants';
import type { Filter } from '@/serverSideTable/materializedTables/materializedTables.constants';
import type {
  SourceScalingTableFilterModel,
  SourceScalingTableFilterOption,
} from '@/tableDefinitionEditor/sourceScalingTableFilter.types';
import type { TableDefinitionOutputV1 } from '@seeqdev/api/model/TableDefinitionOutputV1';

const SOURCE_SCALING_TABLE_ID_COLUMN = SeeqNames.MaterializedTables.SourceScalingTableIdColumn;

export const normalizeSourceScalingTableIds = (ids: readonly string[]): string[] =>
  [...new Set(ids.map((id) => id.trim().toLowerCase()).filter((id) => id.length > 0))].sort();

export const normalizeSourceScalingTableFilterModel = (
  model: SourceScalingTableFilterModel,
): SourceScalingTableFilterModel | null => {
  const sourceTableDefinitionIds = normalizeSourceScalingTableIds(model.sourceTableDefinitionIds);
  const isValidOnlySelection = model.mode === 'ONLY' && sourceTableDefinitionIds.length === 1;
  const isValidExcludeSelection = model.mode === 'EXCLUDE' && sourceTableDefinitionIds.length > 0;
  if (!isValidOnlySelection && !isValidExcludeSelection) {
    return null;
  }
  return { ...model, sourceTableDefinitionIds };
};

export const sourceScalingTableFilterToTableFilter = (model: SourceScalingTableFilterModel): Filter | null => {
  const normalized = normalizeSourceScalingTableFilterModel(model);
  if (!normalized) {
    return null;
  }
  const filterType = normalized.mode === 'ONLY' ? GraphQL.FilterTypes.In : GraphQL.FilterTypes.NotIn;
  return orFilters(
    isNull(SOURCE_SCALING_TABLE_ID_COLUMN),
    valueFilter(SOURCE_SCALING_TABLE_ID_COLUMN, filterType, normalized.sourceTableDefinitionIds),
  );
};

export const getEligibleSourceScalingTableIds = (searches: readonly Search[]): string[] =>
  normalizeSourceScalingTableIds(
    searches.flatMap((search) =>
      isScalingTableSearch(search) &&
      search.pathOverride === true &&
      search.isInclude === true &&
      search.tableDefinitionId
        ? [search.tableDefinitionId]
        : [],
    ),
  );

export const sourceScalingTableFilterOptions = ({
  searches,
  tableDefinitions,
  selectedIds = [],
  unavailableSourceLabel,
}: {
  searches: readonly Search[];
  tableDefinitions: readonly TableDefinitionOutputV1[];
  selectedIds?: readonly string[];
  unavailableSourceLabel: (id: string) => string;
}): SourceScalingTableFilterOption[] => {
  const configuredIds = getEligibleSourceScalingTableIds(searches);
  const ids = normalizeSourceScalingTableIds([...configuredIds, ...selectedIds]);
  const definitionsById = new Map(
    tableDefinitions.map((tableDefinition) => [tableDefinition.id.toLowerCase(), tableDefinition]),
  );
  return [...ids]
    .map((id) => {
      const tableDefinition = definitionsById.get(id);
      const name = tableDefinition?.name?.trim();
      return {
        id,
        label: name || unavailableSourceLabel(id),
      };
    })
    .sort((left, right) => left.label.localeCompare(right.label) || left.id.localeCompare(right.id));
};

const isSourceScalingTableColumn = (columnName: string): boolean =>
  columnName.trim().toLowerCase() === SOURCE_SCALING_TABLE_ID_COLUMN.toLowerCase();

const isSourceScalingTableLeaf = (filter: Filter): boolean => {
  if ('valueFilter' in filter) {
    return isSourceScalingTableColumn(filter.valueFilter.columnName);
  }
  return 'simpleFilter' in filter && isSourceScalingTableColumn(filter.simpleFilter.columnName);
};

const sourceScalingTableFilterModelFromBranch = (filter: Filter): SourceScalingTableFilterModel | null => {
  if (!('compositeFilter' in filter) || filter.compositeFilter.operation !== GraphQL.Operations.Or) {
    return null;
  }
  const children = [filter.compositeFilter.filter1, filter.compositeFilter.filter2];
  const nullChild = children.find(
    (child) =>
      'simpleFilter' in child &&
      isSourceScalingTableColumn(child.simpleFilter.columnName) &&
      child.simpleFilter.filterType === GraphQL.FilterTypes.IsNull,
  );
  const valueChild = children.find(
    (child) =>
      'valueFilter' in child &&
      isSourceScalingTableColumn(child.valueFilter.columnName) &&
      (child.valueFilter.filterType === GraphQL.FilterTypes.In ||
        child.valueFilter.filterType === GraphQL.FilterTypes.NotIn),
  );
  if (!nullChild || !valueChild || !('valueFilter' in valueChild)) {
    return null;
  }
  const value = valueChild.valueFilter.value;
  const values = value !== null && typeof value === 'object' && !Array.isArray(value) ? value.value : value;
  if (!Array.isArray(values) || !values.every((id): id is string => typeof id === 'string')) {
    return null;
  }
  return normalizeSourceScalingTableFilterModel({
    mode: valueChild.valueFilter.filterType === GraphQL.FilterTypes.In ? 'ONLY' : 'EXCLUDE',
    sourceTableDefinitionIds: values,
  });
};

/** Restores the source selector from its saved branch, optionally ANDed with ordinary column filters. */
export const restoreHierarchySourceFilter = (filter: Filter | undefined): SourceScalingTableFilterModel | null => {
  if (!filter) return null;
  const exactBranch = sourceScalingTableFilterModelFromBranch(filter);
  if (exactBranch) {
    return exactBranch;
  }
  if ('compositeFilter' in filter && filter.compositeFilter.operation === GraphQL.Operations.And) {
    return (
      restoreHierarchySourceFilter(filter.compositeFilter.filter1) ??
      restoreHierarchySourceFilter(filter.compositeFilter.filter2)
    );
  }
  return null;
};

/** Removes source predicates while retaining the hierarchy's ordinary column-filter tree. */
export const withoutSourceScalingTableFilter = (filter: Filter): Filter | undefined => {
  if (isSourceScalingTableLeaf(filter)) {
    return undefined;
  }
  if (!('compositeFilter' in filter)) {
    return filter;
  }

  const filter1 = withoutSourceScalingTableFilter(filter.compositeFilter.filter1);
  const filter2 = withoutSourceScalingTableFilter(filter.compositeFilter.filter2);
  if (!filter1) {
    return filter2;
  }
  if (!filter2) {
    return filter1;
  }
  return { compositeFilter: { ...filter.compositeFilter, filter1, filter2 } };
};

/** Separates the source predicate from filters that can be represented and persisted by the table's AG Grid model. */
export const withoutSourceScalingTableFilterSpec = (filterSpec: FilterSpecV1 | undefined): FilterSpecV1 | undefined => {
  const filter = filterSpec?.filter as Filter | undefined;
  if (!filter) {
    return filterSpec;
  }
  const remainingFilter = withoutSourceScalingTableFilter(filter);
  return remainingFilter ? { ...filterSpec, filter: toSdkFilter(remainingFilter) } : undefined;
};
